import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  StatusType,
  createBaseSelector,
  registerSlice
} from '@shared/lib/redux'
import { AgeFilterType, GenderFilterType } from '@shared/types/filters'
import { AnyDiagramEntry } from '@shared/types/geoTypes'

import { UserModel } from '../domain/user.model'
import { UserAddressType, UsersByAddressDTO, UsersRequestBody } from '../types'
import { getFilteredUsersForReport } from '../user.lib'

export interface UsersStore {
  users: UserModel[]
  addresses: UserAddressType[]
  ageData: AnyDiagramEntry[]
  genderData: AnyDiagramEntry[]
  totalUsers: number
  totalAddresses: number
  isApproximateCount: boolean

  isDiagramsOpened: boolean
  isSearchOpened: boolean
  isFiltersOpened: boolean

  genderFilter: GenderFilterType
  ageFilter: AgeFilterType[]

  genderReportFilter: GenderFilterType
  ageReportFilter: AgeFilterType[]
  phoneReportFilter: string

  status: StatusType
  error: string | null
}

const initialState: UsersStore = {
  users: [],
  addresses: [],
  ageData: [],
  genderData: [],

  totalUsers: 0,
  totalAddresses: 0,
  isApproximateCount: false,

  isDiagramsOpened: true,
  isSearchOpened: true,
  isFiltersOpened: true,

  genderFilter: 'any',
  ageFilter: ['from18to25', 'from25to35', 'from35to45', 'from45more'],

  genderReportFilter: 'any',
  ageReportFilter: ['from18to25', 'from25to35', 'from35to45', 'from45more'],
  phoneReportFilter: '',

  status: 'idle',
  error: null
}

const usersSlice = createSlice({
  name: 'usersSlice',
  initialState,
  reducers: {
    getUsersStart(state, _action: PayloadAction<UsersRequestBody>) {
      state.status = 'loading'
    },
    getUsersSuccess(state, action: PayloadAction<UsersByAddressDTO>) {
      state.users = action.payload.users
      state.addresses = action.payload.addresses
      state.ageData = action.payload.ageData
      state.genderData = action.payload.genderData
      state.totalAddresses = action.payload.totalAddresses
      state.totalUsers = action.payload.totalUsers
      state.isApproximateCount = action.payload.isApproximateCount
      state.status = 'success'
    },
    getUsersFailure(state, action: PayloadAction<string>) {
      state.error = action.payload
      state.status = 'error'
    },

    setAgeFilter(state, action: PayloadAction<AgeFilterType[]>) {
      state.ageFilter = action.payload
      state.ageReportFilter = action.payload
    },
    setGenderFilter(state, action: PayloadAction<GenderFilterType>) {
      state.genderFilter = action.payload
      state.genderReportFilter = action.payload
    },
    // ------------------------------- REPORT ---------------------------
    setAgeReportFilter(state, action: PayloadAction<AgeFilterType[]>) {
      state.ageReportFilter = action.payload
    },
    setGenderReportFilter(state, action: PayloadAction<GenderFilterType>) {
      state.genderReportFilter = action.payload
    },
    setPhoneReportFilter(state, action: PayloadAction<string>) {
      state.phoneReportFilter = action.payload
    },
    // ------------------------------- REPORT ---------------------------

    toggleDiagramsOpen(state, action: PayloadAction<boolean>) {
      state.isDiagramsOpened = action.payload
    },
    toggleSearchOpen(state, action: PayloadAction<boolean>) {
      state.isSearchOpened = action.payload
    },
    toggleFiltersOpen(state, action: PayloadAction<boolean>) {
      state.isFiltersOpened = action.payload
    },

    reset(state) {
      state.users = []
      state.addresses = []
      state.ageData = []
      state.genderData = []
      state.totalAddresses = 0
      state.totalUsers = 0

      state.status = 'idle'
      state.error = null
    }
  }
})

const usersBaseSelector = createBaseSelector(usersSlice)
registerSlice([usersSlice])

const selectUsersAndAddressesList = createSelector(usersBaseSelector, (s) => {
  return {
    addressCount: s.totalAddresses,
    total: s.totalUsers,
    status: s.status,
    error: s.error,
    ageData: s.ageData,
    genderData: s.genderData,
    addresses: s.addresses,
    users: s.users,
    isApproximateCount: s.isApproximateCount
  }
})

const selectUsersGender = createSelector(usersBaseSelector, (s) => {
  return s.genderFilter
})

const selectUsersAge = createSelector(usersBaseSelector, (s) => {
  return s.ageFilter
})

const selectIsDiagramsOpened = createSelector(usersBaseSelector, (s) => {
  return s.isDiagramsOpened
})

const selectIsSearchOpened = createSelector(usersBaseSelector, (s) => {
  return s.isSearchOpened
})

const selectIsFiltersOpened = createSelector(usersBaseSelector, (s) => {
  return s.isFiltersOpened
})

const selectUsersAll = createSelector(usersBaseSelector, (s) => {
  const users = s.users
  return {
    users: users,
    ageData: s.ageData,
    genderData: s.genderData,
    total: s.totalUsers,
    status: s.status,
    error: s.error
  }
})

const selectReportUsers = createSelector(usersBaseSelector, (s) => {
  const users = s.users
  const ageReportFilter = s.ageReportFilter
  const genderReportFilter = s.genderReportFilter
  const phoneReportFilter = s.phoneReportFilter
  const filteredUsers = getFilteredUsersForReport(
    users,
    ageReportFilter,
    genderReportFilter,
    phoneReportFilter
  )
  return {
    users: filteredUsers,
    filteredCount: filteredUsers.length,
    count: users.length
  }
})

const selectIsReportFilterUsed = createSelector(usersBaseSelector, (s) => {
  const ageFilter = s.ageFilter
  const ageReportFilter = s.ageReportFilter
  const genderFilter = s.genderFilter
  const genderReportFilter = s.genderReportFilter

  return (
    JSON.stringify(ageFilter) !== JSON.stringify(ageReportFilter) ||
    JSON.stringify(genderFilter) !== JSON.stringify(genderReportFilter)
  )
})

const selectReportPhoneFilter = createSelector(usersBaseSelector, (s) => {
  return s.phoneReportFilter
})
const selectReportAgeFilter = createSelector(usersBaseSelector, (s) => {
  return s.ageReportFilter
})
const selectReportGenderFilter = createSelector(usersBaseSelector, (s) => {
  return s.genderReportFilter
})

const selectGenderReportDisabledValues = createSelector(
  usersBaseSelector,
  (s) => {
    const genderFilter = s.genderFilter
    if (genderFilter === 'male') {
      return ['female', 'any'] as Partial<GenderFilterType>[]
    }
    if (genderFilter === 'female') {
      return ['male', 'any'] as Partial<GenderFilterType>[]
    }
    return []
  }
)

const selectAgeReportAllowedValues = createSelector(usersBaseSelector, (s) => {
  return s.ageFilter
})

export const usersStore = {
  actions: { ...usersSlice.actions },
  selectors: {
    selectUsersGender,
    selectUsersAge,
    selectUsersAll,
    selectUsersAndAddressesList,
    selectIsDiagramsOpened,
    selectIsSearchOpened,
    selectIsFiltersOpened,
    selectReportUsers,
    selectIsReportFilterUsed,
    selectGenderReportDisabledValues,
    selectReportPhoneFilter,
    selectReportAgeFilter,
    selectReportGenderFilter,
    selectAgeReportAllowedValues
  },
  name: usersSlice.name
}
